import * as Sentry from '@sentry/nuxt'
import { isChunkLoadError } from '~/utils/chunkErrors'
import { classifyNetworkError } from '~/utils/networkError'

const config = useRuntimeConfig()
const disableSentry = import.meta.dev || ['local', 'dev'].includes(String(config.public.APP_ENV))

let fromBfcache = false
let tabHiddenAt: number | null = null
let maxHiddenDurationMs = 0

if (import.meta.client) {
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
        if (event.persisted) {
            fromBfcache = true
            window.location.href = window.location.href
        }
    })

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            tabHiddenAt = Date.now()
        } else if (tabHiddenAt !== null) {
            maxHiddenDurationMs = Math.max(maxHiddenDurationMs, Date.now() - tabHiddenAt)
            tabHiddenAt = null
        }
    })

    // Detect post-reload recovery session and set global Sentry scope immediately
    try {
        const raw = sessionStorage.getItem('__hc_chunk_reload_meta__')
        if (raw) {
            const meta: { ts: number; path: string; count: number } = JSON.parse(raw)
            sessionStorage.removeItem('__hc_chunk_reload_meta__')
            Sentry.setTag('prev_chunk_error', 'true')
            Sentry.setTag('prev_chunk_error_path', meta.path)
            Sentry.setTag('prev_chunk_error_sec_ago', String(Math.round((Date.now() - meta.ts) / 1000)))
            Sentry.setTag('prev_chunk_error_reload_n', String(meta.count))
            Sentry.setTag('user_navigated_after_reload', 'false')
        }
    } catch {}
}

function getConnectionInfo() {
    if (!import.meta.client || typeof navigator === 'undefined') {
        return {}
    }

    const nav = navigator as Navigator & {
        connection?: {
            effectiveType?: string
            downlink?: number
            rtt?: number
            saveData?: boolean
        }
        deviceMemory?: number
    }

    return {
        online: navigator.onLine,
        language: navigator.language,
        languages: navigator.languages ?? [],
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        hardwareConcurrency: navigator.hardwareConcurrency,
        deviceMemoryGb: nav.deviceMemory,
        effectiveType: nav.connection?.effectiveType,
        downlinkMbps: nav.connection?.downlink,
        rttMs: nav.connection?.rtt,
        saveData: nav.connection?.saveData,
    }
}

function getPageInfo() {
    if (!import.meta.client || typeof window === 'undefined' || typeof document === 'undefined') {
        return {}
    }

    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined

    return {
        href: window.location.href,
        path: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        referrer: document.referrer || null,
        visibilityState: document.visibilityState,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        navigationType: navEntry?.type,
        domCompleteMs: navEntry?.domComplete,
        loadEventEndMs: navEntry?.loadEventEnd,
        pageAgeSec: Math.round((Date.now() - (performance.timing?.navigationStart ?? Date.now())) / 1000),
        fromBfcache,
        tabHiddenMaxSec: Math.round(maxHiddenDurationMs / 1000),
        scrollY: window.scrollY,
        docReadyState: document.readyState,
        serviceWorkerActive: !!navigator.serviceWorker?.controller,
        memoryUsedMb: (() => {
            const mem = (performance as any).memory
            return mem ? Math.round(mem.usedJSHeapSize / 1024 / 1024) : null
        })(),
        chunkReloadCount: (() => {
            try {
                return JSON.parse(sessionStorage.getItem('__hc_chunk_reloads__') || '{"count":0}').count
            } catch { return 0 }
        })(),
    }
}

function getRecentHttpBreadcrumbs(event: Parameters<NonNullable<typeof Sentry.init>>[0]['beforeSend'] extends (...args: infer A) => any ? A[0] : any) {
    const crumbs = (event?.breadcrumbs ?? [])
        .filter((crumb: any) => crumb?.type === 'http' || crumb?.category === 'xhr' || crumb?.category === 'fetch')
        .slice(-6)
        .map((crumb: any) => ({
            type: crumb.type ?? null,
            category: crumb.category ?? null,
            level: crumb.level ?? null,
            timestamp: crumb.timestamp ?? null,
            method: crumb.data?.method ?? null,
            statusCode: crumb.data?.status_code ?? null,
            url: crumb.data?.url ?? null,
        }))

    const last = crumbs[crumbs.length - 1] ?? null

    return { crumbs, last }
}

if (!disableSentry) {
    Sentry.init({
        dsn: config.public.SENTRY_DSN || undefined,
        environment: config.public.SENTRY_ENVIRONMENT || undefined,
        release: config.public.SENTRY_RELEASE || undefined,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: Number(config.public.SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE ?? 1),
        integrations: [
            Sentry.replayIntegration({
                maskAllText: true,
                maskAllInputs: true,
                blockAllMedia: true,
            }),
        ],
        beforeSend(event) {
            const statusCode = event.contexts?.nuxt?.statusCode ?? event.tags?.statusCode
            if (statusCode === 499 || statusCode === '499') {
                return null
            }

            const message = String(event.exception?.values?.[0]?.value || event.message || '')

            // Безумовно дропаємо — 100% не наш код
            if (/^request aborted$/i.test(message.trim())
                || /^temporary network error$/i.test(message.trim())) {
                return null
            }

            // Downstream від chunk load / після успішного reload — не смітимо inbox.
            // У Sentry лишаємо лише явний capture з тегом chunk_recovery_failed (ліміт reload вичерпано).
            if (/couldn't resolve component/i.test(message)) {
                return null
            }

            if (isChunkLoadError({ message }) && event.tags?.chunk_recovery_failed !== 'true') {
                return null
            }

            // Мінімізована невідома помилка — третьостороннє
            if (message.trim() === 'he' || /^callback timeout$/i.test(message.trim())) {
                return null
            }

            // SyntaxError без JS-фреймів (всі frames = HTML-сторінка) —
            // Safari 26 beta не може спарсити dynamic import chunk або cross-origin скрипт
            if (event.exception?.values?.[0]?.type === 'SyntaxError') {
                const allFrames = event.exception.values.flatMap(
                    (v: any) => v?.stacktrace?.frames ?? []
                )
                const hasJsFrame = allFrames.some((f: any) =>
                    typeof f.filename === 'string' && /\.js(\?|$)/.test(f.filename)
                )
                if (!hasJsFrame) {
                    return null
                }
            }

            // Network Error / Failed to fetch — може бути наш API, тому не дропаємо,
            // але знижуємо до warning щоб не смітили в error inbox
            if (/^network error$/i.test(message.trim()) || /^failed to fetch$/i.test(message.trim())) {
                event.level = 'warning'
            }

            // Браузерні обмеження — не наш код
            if (/the operation is insecure/i.test(message)
                || /webkit\.messageHandlers/i.test(message)) {
                return null
            }

            if (/error invoking postmessage.*java object is gone/i.test(message)) {
                return null
            }

            // Vue hydration internals — SSR mismatch, не actionable
            if (/null is not an object.*evaluating ['"]?[a-z]\.(nextSibling|parentNode|suspenseId|subTree|component\.|emitsOptions)['"]?/i.test(message)
                || /cannot read properties of null.*reading ['"]?(insertBefore|nextSibling|suspenseId|next|subTree)['"]?/i.test(message)
                || /can't access property ['"]?(nextSibling|parentNode|next)['"]?.*is null/i.test(message)
                || /failed to execute ['"]?insertBefore['"]? on ['"]?Node['"]?/i.test(message)) {
                return null
            }


            // e.value null — вже було
            if (/null is not an object.*evaluating ['"]?e\.value['"]?/i.test(message)
                || /cannot read properties of null.*reading ['"]?value['"]?/i.test(message)) {
                return null
            }

            const connection = getConnectionInfo()
            const page = getPageInfo()
            const http = getRecentHttpBreadcrumbs(event)

            if (event.request?.headers) {
                delete event.request.headers.cookie
                delete event.request.headers.authorization
                delete event.request.headers['x-xsrf-token']
            }

            if (event.request?.url) {
                event.request.url = event.request.url
                    .replace(/([?&]api_token=)[^&]+/gi, '$1[Filtered]')
                    .replace(/([?&]token=)[^&]+/gi, '$1[Filtered]')
            }

            const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? []
            const externalFrameFound = frames.some((frame) => {
                const file = (frame.filename || '').toLowerCase()
                return file.startsWith('chrome-extension://')
                    || file.includes('instagram.com')
                    || file.includes('googletagmanager.com')
                    || file.includes('klaviyo.com')
                    || file.includes('freshchat')
                    || file.includes('google-analytics.com')
                    || file.includes('facebook.net')
                    || file.includes('doubleclick.net')
                    || file.includes('googleadservices.com')
            })

            if (externalFrameFound) {
                return null
            }

            if (isChunkLoadError({ message })) {
                const chunkUrlMatch = message.match(/https?:\/\/[^\s"']+\/_nuxt\/[^\s"']+/)
                event.tags = {
                    ...event.tags,
                    chunk_load_failed: 'true',
                    ...(chunkUrlMatch?.[0] ? { chunk_url: chunkUrlMatch[0] } : {}),
                }
            }

            const exception = event.exception?.values?.[0]
            const networkLike = classifyNetworkError({
                name: exception?.type,
                message: exception?.value || event.message || '',
            })

            event.tags = {
                ...event.tags,
                network_online: String(connection.online ?? 'unknown'),
                network_effective_type: String(connection.effectiveType ?? 'unknown'),
                network_is_offline: String(networkLike.isOffline),
                network_is_aborted: String(networkLike.isAborted),
                network_is_no_response: String(networkLike.isNetworkNoResponse),
                page_visibility: String(page.visibilityState ?? 'unknown'),
                page_navigation_type: String(page.navigationType ?? 'unknown'),
                page_from_bfcache: String(page.fromBfcache ?? false),
                page_age_sec: String(page.pageAgeSec ?? 0),
                page_tab_hidden_max_sec: String(page.tabHiddenMaxSec ?? 0),
                page_scroll_y: String(page.scrollY ?? 0),
                page_doc_ready_state: String(page.docReadyState ?? 'unknown'),
                page_service_worker: String(page.serviceWorkerActive ?? false),
                ...(page.memoryUsedMb != null ? { page_memory_mb: String(page.memoryUsedMb) } : {}),
                chunk_reload_count: String(page.chunkReloadCount ?? 0),
            }

            event.contexts = {
                ...(event.contexts ?? {}),
                network_diagnostics: {
                    connection,
                    classification: networkLike,
                    lastHttpBreadcrumb: http.last,
                    recentHttpBreadcrumbs: http.crumbs,
                } as any,
                page_diagnostics: page as any,
            }

            if (networkLike.isNetworkNoResponse) {
                const transaction = String(event.transaction || '')
                const isCheckoutFlow = transaction.startsWith('/checkout')

                event.tags = {
                    ...event.tags,
                    network_no_response: 'true',
                    flow: isCheckoutFlow ? 'checkout' : 'browse',
                }

                if (!isCheckoutFlow) {
                    event.level = 'warning'
                }
            }

            return event
        },
    })
}
